/**
 * Quantisation helpers for live recording (SPEC §4.5, §10) — pure functions.
 *
 * Time arrives from the transport in ticks (PPQ 480 → 120 ticks per 16th
 * step). Helpers here convert ticks to fractional steps, snap to the nearest
 * 16th (wrapping at the 64-step loop boundary), derive note lengths from
 * key-down/key-up step pairs, and turn a batch of `RecordedEvent`s into notes
 * ready for a `writeNotes` merge.
 */
import type { Note, RecordedEvent } from '../types';
import { TOTAL_STEPS, clamp } from '../types';

/** Default pulses-per-quarter-note of the transport (Tone.js). */
export const DEFAULT_PPQ = 480;

/**
 * Convert transport ticks to fractional 16th steps. At the default PPQ of 480
 * one step is 120 ticks, so `ticksToStep(180)` is `1.5`.
 *
 * @param ticks transport position in ticks (may be fractional)
 * @param ppq   pulses per quarter note; defaults to 480
 */
export function ticksToStep(ticks: number, ppq: number = DEFAULT_PPQ): number {
  return ticks / (ppq / 4);
}

/**
 * Snap a fractional step to the nearest integer step, wrapped into `0..63`.
 * `0.45 → 0`, `0.55 → 1`, `63.6 → 0`.
 */
export function snapStep(stepFloat: number): number {
  const r = Math.round(stepFloat);
  return ((r % TOTAL_STEPS) + TOTAL_STEPS) % TOTAL_STEPS;
}

/**
 * Length in steps of a note held from `onStep` (key-down) to `offStepFloat`
 * (key-up): `max(1, round(off − on))`, wrapped modulo 64 so an off-step that
 * was reported after the loop restarted still yields the right length.
 * A note held for exactly one whole loop therefore wraps to length 1.
 */
export function noteLengthFromSteps(onStep: number, offStepFloat: number): number {
  let diff = offStepFloat - onStep;
  if (diff < 0) diff += TOTAL_STEPS;
  const rounded = Math.round(diff);
  const wrapped = ((rounded % TOTAL_STEPS) + TOTAL_STEPS) % TOTAL_STEPS;
  return Math.max(1, wrapped);
}

/**
 * Quantise a batch of recorded key events into notes (without `by`; the
 * store fills that in from the apply meta). The on-step is snapped to the
 * nearest 16th, the length comes from `noteLengthFromSteps`, velocity is
 * clamped to `0..1`. When two events land on the same pitch and step, the
 * longer one wins (ties keep the earlier event). Output is sorted by step,
 * then pitch. The input array is not mutated.
 */
export function quantizeEvents(events: readonly RecordedEvent[]): Omit<Note, 'by'>[] {
  const byKey = new Map<string, Omit<Note, 'by'>>();
  for (const e of events) {
    const note: Omit<Note, 'by'> = {
      step: snapStep(e.onStep),
      pitch: e.pitch,
      length: noteLengthFromSteps(e.onStep, e.offStep),
      velocity: clamp(e.velocity, 0, 1),
    };
    const key = `${note.pitch}:${note.step}`;
    const existing = byKey.get(key);
    if (!existing || note.length > existing.length) byKey.set(key, note);
  }
  return [...byKey.values()].sort((a, b) => a.step - b.step || a.pitch - b.pitch);
}
